import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { V } from "@/lib/vocab";

// iter252/M11 — automated test runs leave accounts and duplicate records behind that
// eventually show up on real screens (90 `test_*@example.com` rows on a team page,
// two copies of the same document feeding the AI). Owner-only, and it always shows
// exactly what it found before anything is removed.
export function DataHygieneCard() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/data-hygiene").then((r) => setScan(r.data)).catch(() => setScan(null));
  useEffect(() => { load(); }, []);

  const total = scan ? Object.values(scan.counts || {}).reduce((a, b) => a + b, 0) : 0;
  if (!scan || total === 0) return null;

  const clean = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/data-hygiene/clean", { confirm: true });
      const rm = r.data.removed;
      toast.success("Test data cleaned up", {
        description: `${rm.test_users} accounts · ${rm.duplicate_people} duplicate ${V.lcs("person")} · ${rm.duplicate_documents} duplicate documents`,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="data-hygiene-card" className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 mb-6">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-xl bg-white border border-amber-200 flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-amber-700" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-bold text-ink text-base">Leftover test data found</h3>
          <p className="text-sm text-ink/70 mt-0.5" data-testid="data-hygiene-summary">
            {scan.counts.test_users > 0 && <>{scan.counts.test_users} test account{scan.counts.test_users === 1 ? "" : "s"}</>}
            {scan.counts.test_residents > 0 && <>{scan.counts.test_users > 0 ? " · " : ""}{scan.counts.test_residents} test {V.lc("person")} record{scan.counts.test_residents === 1 ? "" : "s"}</>}
            {scan.counts.duplicate_people > 0 && <>{" · "}{scan.counts.duplicate_people} duplicate {V.lc("person")}{scan.counts.duplicate_people === 1 ? "" : "s"}</>}
            {scan.counts.duplicate_documents > 0 && <>{" · "}{scan.counts.duplicate_documents} duplicate document{scan.counts.duplicate_documents === 1 ? "" : "s"}</>}
          </p>
          <p className="text-xs text-ink/50 mt-1">
            Accounts and {V.lcs("person")} on test markers are deleted; duplicate {V.lcs("person")} are merged to the
            original; duplicate documents are moved to deleted (recoverable) so the concierge stops
            reading the same file twice.
          </p>
        </div>
        <button data-testid="data-hygiene-clean-btn" onClick={clean} disabled={busy}
          className="w-full sm:w-auto rounded-full bg-ink text-white px-4 py-2 text-xs font-semibold hover:bg-ink/90 transition-colors disabled:opacity-60 shrink-0">
          {busy ? "Cleaning…" : "Clean up test data"}
        </button>      </div>
    </div>
  );
}
